import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Users, Shield, Gamepad2, Activity, BookOpen, LogIn } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const navLinks = [
  { path: '/', label: 'Accueil', icon: Gamepad2 },
  { path: '/rules', label: 'Règlement', icon: BookOpen },
  { path: '/join', label: 'Rejoindre', icon: LogIn },
  { path: '/staff', label: 'Staff', icon: Users },
  { path: '/status', label: 'Status', icon: Activity },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'glass-heavy py-3' 
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt={settings.server_name}
                  className="h-10 w-auto"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Shield className="w-8 h-8 text-violet-500" />
                  <span className="font-heading font-bold text-xl uppercase tracking-wider gradient-text">
                    {settings.server_name}
                  </span>
                </div>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                    className={`relative px-4 py-2 text-sm font-medium uppercase tracking-widest transition-colors duration-200 flex items-center gap-2 ${
                      isActive 
                        ? 'text-violet-400' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-500"
                        initial={false}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Discord Button (Desktop) */}
            <a
              href={settings.discord_link}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider px-6 py-2.5 btn-skew transition-all duration-300 hover:shadow-[0_0_20px_rgba(139,92,246,0.5)]"
              data-testid="nav-discord-btn"
            >
              <span>Discord</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setIsMobileMenuOpen(false)} />
            <div className="absolute top-20 left-4 right-4 glass rounded-xl p-4">
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  const isActive = location.pathname === link.path;
                  
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-violet-600/20 text-violet-400' 
                          : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-heading font-semibold uppercase tracking-wider">
                        {link.label}
                      </span>
                    </Link>
                  );
                })}
                
                <a
                  href={settings.discord_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider px-6 py-3 rounded-lg mt-2 transition-all"
                  data-testid="mobile-discord-btn"
                >
                  Discord
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
